import { useMemo, useState } from 'react';
import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { closePanelTabSelector, setPanelSelector } from '@/state/selector/panelSelector';
import { userAtom, userPermissionToViewKnowledgeAtom } from '@/state/atom/userAtom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { IconButton, Menu, MenuItem } from '@material-ui/core';
import LogKibanaConstant from '@/common/constant/LogKibanaConstant';
import { helpdeskTicketLastUserActionAtom, isShowVisibilityKnowledgeAtom } from '@/state/atom/ticketAtom';
import { multiPanelAtom } from '@/state/atom/panelAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import CorporationUtils from '@/common/utils/CorporationUtils';
import ChatPostOnDemandControl from '@/component/multiLanguage/elements/ChatPostOnDemandControl';
import useOnDemandOutlineDisplay from '@/component/multiLanguage/hooks/useOnDemandOutlineDisplay';
import OverflowTooltipContent from '@/component/common/OverflowTooltipContent';
import { generateKnowledgeOutlineOnTabChat } from '@/component/ticketList/handler/TicketHandler';

export const TicketKnowledgeHead = ({ ...props }) => {
    const panelList = useRecoilValue(multiPanelAtom);
    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    const addNewPanel = useSetRecoilState(setPanelSelector);
    const setLastUserAction = useSetRecoilState(helpdeskTicketLastUserActionAtom);

    const corporationDetail = useRecoilValue(corporationAtom);

    // Header options dropdown (⋮) anchor.
    const [anchorHeadOptions, setAnchorHeadOptions] = useState(null);
    const isOpenHeadOptions = Boolean(anchorHeadOptions);

    // Show the 翻訳版 toggle only when the org has multilingual ON and the thread
    // is not the AI Assistant thread (AI already replies in the user's language).
    const isShowTranslationToggle =
        CorporationUtils.isEnableSettingMultiLanguage(corporationDetail) &&
        props?.selectThread?.threadType !== Constant.THREAD_TYPE.AI_ASSISTANT;

    // common userDetail state
    const userDetail = useRecoilValue(userAtom);
    const userPermissionView = useRecoilValue(userPermissionToViewKnowledgeAtom);
    const setIsShowVisibilityKnowledgeAtom = useSetRecoilState(isShowVisibilityKnowledgeAtom);

    const titleOfKnowledgeInfo = useMemo(() => {
        return generateKnowledgeOutlineOnTabChat(props?.ticketInfo)
    }, [props?.ticketInfo]);

    // On-demand translation: show the translated title while this thread's
    // 「翻訳版」toggle is ON, otherwise the original outline.
    const { displayOutline: titleToDisplay } = useOnDemandOutlineDisplay(
        titleOfKnowledgeInfo,
        props?.chatThreadId,
    );

    const onOpenKnowledge = () => {
        const existedKnowledgeHelpdeskTicketListPanel = panelList.find(
            panel => (Constant.PANEL_TYPE.KNOWLEDGE_HELPDESK_TICKET_LIST, Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST).includes(panel.type)
        );
        
        if (!Utils.isEmptyObject(existedKnowledgeHelpdeskTicketListPanel)) {
            closePanelTab({
                event: null,
                panelType: existedKnowledgeHelpdeskTicketListPanel.type,
                panelId: existedKnowledgeHelpdeskTicketListPanel.id,
                tabId: 'knowledge_ticket_list_tab'
            });
        }

        let detailTicket = props.ticketInfo;
        let addNewPanelParams = {
            type: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST,
            componentName: Constant.COMPONENT_NAME.LIST_TICKET_KNOWLEDGE,
            tabId: 'knowledge_ticket_list_tab',
            visibilitySettingType: Constant.VISIBILITY_SETTING_TYPE.PRIVATE,
            isCustomer: false,
            customerId: '',
            projectSelectedId: userPermissionView.projectSelectedId,
            corporationSelectedId: userPermissionView.corporationSelectedId,
            props : {
                ...props,
                componentName: Constant.COMPONENT_NAME.LIST_TICKET_KNOWLEDGE,
                panelType: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST
            }
        }
        if (
            userPermissionView.projectSelectedId !== detailTicket.affiliationProject &&
            (userDetail?.id === detailTicket.customer || userPermissionView.projectSelectedId === detailTicket.customer)
        ) {
            setIsShowVisibilityKnowledgeAtom(false);

            addNewPanelParams.visibilitySettingType = Constant.VISIBILITY_SETTING_TYPE.CUSTOMER
            addNewPanelParams.isCustomer = true
            addNewPanelParams.customerId = detailTicket.customer
            addNewPanelParams.projectSelectedId = detailTicket.affiliationProject
            addNewPanelParams.corporationSelectedId = detailTicket.affiliationOrganization
        }
        addNewPanel(addNewPanelParams)                                                                          
    };

    const viewTicketKnowledgeDetail = async () => {
        if (!props.ticketInfo.id) {
            return;
        }

        addNewPanel({
            // type: isProjectInuse ? Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL :Constant.PANEL_TYPE.KNOWLEDGE_HELPDESK_TICKET_DETAIL,
            type: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_DETAIL,
            tabId: props.ticketInfo.id,
            props,
            ticketId: props.ticketInfo.id,
            isCustomer: false,
            isTicketDeleted: !Utils.isEmptyString(props.ticketInfo?.deletedAt) ? true : false
        });
        if (setLastUserAction) {
            setLastUserAction(LogKibanaConstant.CONTENT_OPERATION_ACTION.VIEW);
        }
    };

    return (
        <div className="headTicket">
            <div className='headTicket-chat headTicket-wrap'>
                <div className='headTicket-fl'>
                    <div className='headline' onClick={viewTicketKnowledgeDetail}>
                        <div className="headline__thumb">
                            <img src="/assets/image/icon-check.svg" alt="" />
                        </div>
                        <div className="headline__content" data-cy="ticket-header-tlt">
                            <OverflowTooltipContent text={titleToDisplay} />
                        </div>
                    </div>
                </div>

                <div className='headTicket-rg'>
                    <div className='headeLink'>
                        <IconButton className='itemTools' onClick={onOpenKnowledge}>
                            <img
                                src="/assets/image/icon-link.svg" alt=""
                            />
                        </IconButton>
                    </div>

                    {isShowTranslationToggle && (
                        <div className="headOptions">
                            <IconButton
                                className="itemTools"
                                aria-label="more"
                                aria-haspopup="true"
                                aria-expanded={isOpenHeadOptions ? 'true' : undefined}
                                onClick={(e) => setAnchorHeadOptions(e.currentTarget)}
                                data-cy="chat-head-options-button"
                            >
                                <img src="/assets/image/icon-more-panel.svg" alt="" />
                            </IconButton>

                            <Menu
                                anchorEl={anchorHeadOptions}
                                open={isOpenHeadOptions}
                                onClose={() => setAnchorHeadOptions(null)}
                                getContentAnchorEl={null}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem disableRipple className="headOptions-item">
                                    <ChatPostOnDemandControl
                                        threadId={props?.chatThreadId}
                                        ticketInfo={props.ticketInfo}
                                    />
                                </MenuItem>
                            </Menu>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
